import { ip_back } from "../config";

function authHeaders({ json = true } = {}) {
  let token =
    (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
  if (!token && typeof localStorage !== "undefined") {
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      token = stored?.user_token || "";
    } catch {
      /* ignore */
    }
  }
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: token } : {}),
  };
}

async function request(path, { method = "GET", body = null, signal, formData = null } = {}) {
  const isForm = formData instanceof FormData;
  const resp = await fetch(`${ip_back}${path}`, {
    method,
    headers: authHeaders({ json: !isForm }),
    body: isForm ? formData : body ? JSON.stringify(body) : undefined,
    signal,
  });
  const json = await resp.json().catch(() => ({}));

  // Some auth failures return HTTP 200 with status:"error" — treat as failure.
  const apiError =
    json &&
    typeof json === "object" &&
    (json.status === "error" || json.status === 0);
  if ((!resp.ok && resp.status !== 202) || apiError) {
    const error = new Error(
      json.message || `Request failed (${resp.status || "error"})`
    );
    error.status = resp.status;
    error.code = json.code || null;
    error.data = json.data || null;
    throw error;
  }
  return json.data;
}

/**
 * Rally windows worth clipping, the metrics, the overlay layout the server
 * will render, and the consent state.
 */
export function getClipCandidates({ teId, userId = null, lang = null, signal } = {}) {
  const params = new URLSearchParams({ te_id: teId });
  if (userId) params.set("user_id", userId);
  if (lang) params.set("lang", lang);
  return request(`api/social-clip/candidates?${params}`, { signal });
}

export function getClipConsentStatus({ teId, tableId = null, purpose = null, signal } = {}) {
  const params = new URLSearchParams({ te_id: teId });
  if (tableId) params.set("table_id", tableId);
  if (purpose) params.set("purpose", purpose);
  return request(`api/media-consent/clip-status?${params}`, { signal });
}

/**
 * `files` are the pages of one signed form (photos and/or PDF), in order.
 * `payload.purposes` may list several purposes cleared by that same form.
 */
export function grantMediaConsent(payload, { signal, file = null, files = null } = {}) {
  const pages = [...(files || []), ...(file ? [file] : [])].filter(Boolean);
  if (pages.length) {
    const form = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value == null || value === "") return;
      form.append(key, Array.isArray(value) ? JSON.stringify(value) : String(value));
    });
    pages.forEach((page) => form.append("documents", page, page.name));
    return request("api/media-consent/grant", {
      method: "POST",
      formData: form,
      signal,
    });
  }
  return request("api/media-consent/grant", {
    method: "POST",
    body: payload,
    signal,
  });
}

export function revokeMediaConsent(payload, { signal } = {}) {
  return request("api/media-consent/revoke", {
    method: "POST",
    body: payload,
    signal,
  });
}

export function startClipRender(payload, { signal } = {}) {
  return request("api/social-clip/render", {
    method: "POST",
    body: payload,
    signal,
  });
}

export function getClip(clipId, { signal } = {}) {
  return request(`api/social-clip/${encodeURIComponent(clipId)}`, { signal });
}

export function updateClip(clipId, payload, { signal } = {}) {
  return request(`api/social-clip/${encodeURIComponent(clipId)}`, {
    method: "PATCH",
    body: payload,
    signal,
  });
}

export function generateCaption(clipId, payload = {}, { signal } = {}) {
  return request(`api/social-clip/${encodeURIComponent(clipId)}/caption`, {
    method: "POST",
    body: payload,
    signal,
  });
}

export function listSocialAccounts({ clubId = null, signal } = {}) {
  const params = new URLSearchParams();
  if (clubId) params.set("club_id", clubId);
  return request(`api/social-publish/accounts?${params}`, { signal });
}

export function startOAuthConnect(platform, { clubId = null, signal } = {}) {
  const params = new URLSearchParams();
  if (clubId) params.set("club_id", clubId);
  return request(
    `api/social-publish/oauth/${encodeURIComponent(platform)}/start?${params}`,
    { signal }
  );
}

export function disconnectSocialAccount(accountId, { signal } = {}) {
  return request(`api/social-publish/accounts/${encodeURIComponent(accountId)}`, {
    method: "DELETE",
    signal,
  });
}

export function publishClip(payload, { signal } = {}) {
  return request("api/social-publish/post", {
    method: "POST",
    body: payload,
    signal,
  });
}

export function listClipPosts(clipId, { signal } = {}) {
  return request(
    `api/social-publish/posts?clip_id=${encodeURIComponent(clipId)}`,
    { signal }
  );
}

/** Public share URL for a rendered clip. */
export function clipShareUrl(shareToken) {
  return `${ip_back}api/public/clip/${encodeURIComponent(shareToken)}`;
}

/**
 * Poll a clip until it leaves the rendering state.
 * @returns {Promise<object>} the ready clip
 */
export async function pollClipUntilReady(
  clipId,
  { onProgress, signal, intervalMs = 2000, maxAttempts = 300 } = {}
) {
  for (let i = 0; i < maxAttempts; i += 1) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const clip = await getClip(clipId, { signal });
    onProgress?.(clip);

    if (clip.status === "ready" || clip.status === "published") return clip;
    if (clip.status === "failed") {
      throw new Error(clip.error_message || "Clip render failed");
    }

    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("Clip render timed out");
}

export function selfGrantMediaConsent(payload, { signal } = {}) {
  return request("api/media-consent/self-grant", {
    method: "POST",
    body: payload,
    signal,
  });
}

export function getUserMediaConsents(userId, { signal } = {}) {
  return request(`api/media-consent/user/${encodeURIComponent(userId)}`, { signal });
}

export function getClubConsentSummary({ signal } = {}) {
  return request("api/media-consent/club-summary", { signal });
}

export function recordLegalAcceptance(documents, { signal } = {}) {
  return request("api/media/legal-acceptance", {
    method: "POST",
    body: { documents },
    signal,
  });
}

/** Record current privacy + terms versions. Never fail the signup if this write misses. */
export async function recordPrivacyAndTermsAcceptance({ signal } = {}) {
  try {
    await recordLegalAcceptance(["PRIVACY", "TERMS"], { signal });
  } catch (err) {
    console.warn("Legal acceptance not recorded:", err?.message);
  }
}

export default {
  getClipCandidates,
  getClipConsentStatus,
  grantMediaConsent,
  revokeMediaConsent,
  startClipRender,
  getClip,
  updateClip,
  generateCaption,
  listSocialAccounts,
  startOAuthConnect,
  disconnectSocialAccount,
  publishClip,
  listClipPosts,
  clipShareUrl,
  pollClipUntilReady,
};
